import React, { useEffect, useRef, useState } from 'react';
import ScreenCapture from './ScreenCapture';
import './AttachmentDropzone.css';

/**
 * «المرفقات» — the client portal's attachment box (helpdesk.matix.one → تذكرة
 * جديدة), reproduced for the agent's reply (Mohamed, 2026-10-12: «اريده ان يكون
 * مثل الموجود في الهلب ديسك بالظبط»):
 *   - click or drag & drop onto the dashed box;
 *   - Ctrl+V pastes an image from the clipboard (anywhere on the page, as in the
 *     portal), downscaled to 2560 px like the portal does;
 *   - «التقاط صورة للشاشة» — the portal's ScreenCapture (with its editor);
 *   - the list shows a thumbnail for images and a remove button.
 *
 * The limits are the agent reply endpoint's (backend helpdesk-agent.routes.js:
 * 10 MB a file); 5 files, as before. A file over the limit is now REPORTED —
 * the previous box dropped it silently.
 */
const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;

const TEXT = {
    ar: {
        label: 'المرفقات',
        dragDrop: 'انقر للرفع أو اسحب وافلت',
        types: 'JPG, PNG, GIF, PDF, DOC, XLS حتى 10 ميجابايت (5 ملفات كحد أقصى)',
        paste: 'أو اضغط Ctrl+V للصق صورة',
        screenshot: 'التقاط صورة للشاشة',
        tooBig: (name) => `الملف "${name}" يتجاوز 10 ميجابايت`,
        tooMany: 'الحد الأقصى 5 ملفات',
    },
    en: {
        label: 'Attachments',
        dragDrop: 'Click to upload or drag & drop',
        types: 'JPG, PNG, GIF, PDF, DOC, XLS up to 10MB (max 5 files)',
        paste: 'Or press Ctrl+V to paste image',
        screenshot: 'Take Screenshot',
        tooBig: (name) => `File "${name}" exceeds 10MB limit`,
        tooMany: 'Maximum 5 files allowed',
    },
};

/** The portal's paste compression: images over 2560 px are scaled down, saved as JPEG 0.92. */
function compressImage(file) {
    return new Promise((resolve) => {
        if (!file.type.startsWith('image/')) return resolve(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                let { width, height } = img;
                const maxDim = 2560;
                if (width > maxDim || height > maxDim) {
                    if (width > height) { height = (height / width) * maxDim; width = maxDim; }
                    else { width = (width / height) * maxDim; height = maxDim; }
                }
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                    (blob) => {
                        if (!blob) return resolve(file);
                        // .jpg, so the name agrees with the bytes.
                        const name = (file.name || 'pasted-image').replace(/\.[^.]+$/, '') + '.jpg';
                        resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
                    },
                    'image/jpeg',
                    0.92
                );
            };
            img.onerror = () => resolve(file);
            img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
    });
}

function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentDropzone({ files, onChange, language = 'en' }) {
    const t = TEXT[language === 'ar' ? 'ar' : 'en'];
    const inputRef = useRef(null);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [showCapture, setShowCapture] = useState(false);

    // The latest list, for the document-level paste listener.
    const filesRef = useRef(files);
    filesRef.current = files;
    // ScreenCapture takes Ctrl+V itself while it is open; one paste, one image.
    const captureOpenRef = useRef(false);
    captureOpenRef.current = showCapture;

    const add = (incoming) => {
        const current = filesRef.current;
        const ok = [];
        for (const f of incoming) {
            if (f.size > MAX_BYTES) { setError(t.tooBig(f.name)); continue; }
            ok.push(f);
        }
        if (ok.length === 0) return;
        if (current.length + ok.length > MAX_FILES) {
            setError(t.tooMany);
            const room = MAX_FILES - current.length;
            if (room > 0) onChange([...current, ...ok.slice(0, room)]);
            return;
        }
        setError('');
        onChange([...current, ...ok]);
    };

    useEffect(() => {
        const onPaste = async (e) => {
            if (captureOpenRef.current) return;
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
                if (item.type.startsWith('image/')) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) add([await compressImage(file)]);
                    break;
                }
            }
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    // Clear a stale message once the list is emptied (e.g. after sending).
    useEffect(() => { if (files.length === 0) setError(''); }, [files.length]);

    const previews = files.map((f) => (f.type && f.type.startsWith('image/') ? URL.createObjectURL(f) : null));
    useEffect(() => () => previews.forEach((u) => u && URL.revokeObjectURL(u)));

    return (
        <div className="hd-attach">
            <label className="hd-attach-label">{t.label}</label>
            <div
                className={`hd-attach-dropzone${dragOver ? ' drag-over' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); add(Array.from(e.dataTransfer.files)); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
            >
                <input
                    type="file"
                    ref={inputRef}
                    onChange={(e) => { add(Array.from(e.target.files)); e.target.value = ''; }}
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    style={{ display: 'none' }}
                />
                <div className="hd-attach-body">
                    <span className="hd-attach-icon">📎</span>
                    <p>{t.dragDrop}</p>
                    <span className="hd-attach-hint">{t.types}</span>
                    <span className="hd-attach-hint">{t.paste}</span>
                </div>
            </div>

            <button type="button" className="hd-attach-screenshot" onClick={() => setShowCapture(true)} disabled={files.length >= MAX_FILES}>
                📷 {t.screenshot}
            </button>

            {error && <div className="hd-attach-error">{error}</div>}

            {files.length > 0 && (
                <div className="hd-attach-list">
                    {files.map((file, i) => (
                        <div key={`${file.name}-${i}`} className="hd-attach-item">
                            {previews[i] ? (
                                <div className="hd-attach-preview"><img src={previews[i]} alt={file.name} /></div>
                            ) : (
                                <span className="hd-attach-file-icon">📄</span>
                            )}
                            <div className="hd-attach-info">
                                <span className="hd-attach-name">{file.name}</span>
                                <span className="hd-attach-size">{formatFileSize(file.size)}</span>
                            </div>
                            <button type="button" className="hd-attach-remove" onClick={() => onChange(files.filter((_, j) => j !== i))}>×</button>
                        </div>
                    ))}
                </div>
            )}

            {showCapture && (
                <ScreenCapture
                    onCapture={(shots) => { add(shots); setShowCapture(false); }}
                    onClose={() => setShowCapture(false)}
                    language={language}
                    maxImages={MAX_FILES - files.length}
                />
            )}
        </div>
    );
}
